/**
 * Fixture type normalization and display utilities.
 * Used by multi-select to group fixtures by category.
 */

const { SINK_TYPE } = require('../../sink-types');
const {
  GENERIC_TYPES: _GENERIC_TYPES,
  getObjectType: _getObjectType,
} = require('../../fixture-types');

// Re-export as static `export const` bindings so webpack can see them through
// the CJS boundary (`shared-lib/fixture-types.js` uses module.exports). Plain
// `export { ... }` of CJS-imported names isn't statically resolvable and fails
// in downstream importers with "not found" warnings.
const getObjectType = _getObjectType;
const GENERIC_TYPES = _GENERIC_TYPES;

/**
 * Explicit mapping from exact object type strings to their normalized category.
 * Uses exact matches to avoid substring collisions (e.g., 'bathroom-sink-faucet'
 * contains 'sink' but should normalize to 'faucet', not 'sink').
 */
const TYPE_TO_CATEGORY = {
  // Faucets
  'bathroom-faucet': 'faucet',
  'bathroom-sink-faucet': 'faucet',
  'bathroom-tub-faucet': 'faucet',
  // Sink (canonical type)
  [SINK_TYPE]: 'sink',
  // Toilets
  toilet: 'toilet',
  // Bathtubs
  bathtub: 'bathtub',
  tub: 'bathtub',
  // Mirrors
  mirror: 'mirror',
  // Shower
  showerhead: 'showerhead',
  'shower-controls': 'shower-controls',
  'shower-stem': 'shower-stem',
  shower: 'shower',
  'shower-door': 'shower-door',
  // Cabinets
  'lower-cabinet': 'cabinet',
  'upper-cabinet': 'cabinet',
  'storage-cabinet': 'cabinet',
  storage: 'cabinet',
  vanity: 'cabinet',
  // Lights
  'wall-light': 'light',
  'overhead-wall-light': 'light',
  'ceiling-light': 'light',
  // Countertops
  countertop: 'countertop',
  // Openings
  window: 'window',
  door: 'door',
  // Furniture
  chair: 'chair',
  table: 'table',
  sofa: 'sofa',
  fireplace: 'fireplace',
  'wood-stove': 'wood-stove',
};

/**
 * Normalize an object type string to a canonical fixture category.
 * Uses explicit map for exact matches, no substring guessing.
 */
const normalizeFixtureType = (type) => {
  if (!type) return null;
  const t = type.toLowerCase();
  return TYPE_TO_CATEGORY[t] || t;
};

/**
 * Get a human-readable plural display name for a fixture category.
 */
const DISPLAY_NAMES = {
  sink: 'Sinks',
  toilet: 'Toilets',
  bathtub: 'Bathtubs',
  mirror: 'Mirrors',
  faucet: 'Faucets',
  'bathroom-sink-faucet': 'Faucets',
  'bathroom-tub-faucet': 'Tub Faucets',
  showerhead: 'Showerheads',
  'shower-controls': 'Shower Controls',
  cabinet: 'Cabinets',
  'lower-cabinet': 'Cabinets',
  'upper-cabinet': 'Cabinets',
  light: 'Lights',
  'wall-light': 'Wall Lights',
  'overhead-wall-light': 'Vanity Lights',
  'ceiling-light': 'Ceiling Lights',
  countertop: 'Countertops',
  window: 'Windows',
  door: 'Doors',
};

/**
 * Types with no `DISPLAY_NAMES` entry used to fall through as the RAW type —
 * so the panel read "Apply to all sofa", "Apply to all storage-furniture".
 * 97 of the object types in dev take this path, so the fallback has to be a
 * real name, not the key.
 *
 * English plurals are irregular enough that the real types cover most of the
 * traps: `switch` → Switches, `dorm-bookshelf` → Bookshelves, `storage` is
 * uncountable, `stairs` and `dorm-fairy-lights` are already plural.
 */
const UNCOUNTABLE = new Set([
  'storage',
  'furniture',
  'decor',
  'shelving',
  'seating',
  'lighting',
  'equipment',
  'clothing',
]);

/** Already plural: ends in `s` but is not a singular like `glass` / `axis`. */
const readsAsPlural = (w) =>
  w.endsWith('s') && !/(ss|us|is)$/.test(w) && w.length > 2;

const pluralizeWord = (w) => {
  const lower = w.toLowerCase();
  if (UNCOUNTABLE.has(lower) || readsAsPlural(lower)) return w;
  if (/(s|ss|sh|ch|x|z)$/i.test(w)) return `${w}es`;
  if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`;
  if (/fe$/i.test(w)) return `${w.slice(0, -2)}ves`;
  if (/[^f]f$/i.test(w)) return `${w.slice(0, -1)}ves`;
  return `${w}s`;
};

/** `dorm-desk-chair` / `washerDryer` → ['Dorm','Desk','Chair'] / ['Washer','Dryer'] */
const humanizeWords = (type) =>
  String(type)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));

/**
 * Human-readable SINGULAR name — for naming one item ("Delete this Switch").
 *
 * Derived from the TYPE, never by un-pluralising the plural string: "Switches"
 * and "Niches" both end in "ches" but come from `switch` and `niche`, so no
 * rule can recover the stem from the plural alone. The caller that needs this
 * always has the type, so it never has to guess.
 */
const fixtureTypeDisplayNameSingular = (normalizedType) => {
  const curated = DISPLAY_NAMES[normalizedType];
  // Every curated name is a plain `+s` plural, so the strip is safe there.
  if (curated) return curated.endsWith('s') ? curated.slice(0, -1) : curated;
  if (!normalizedType) return '';
  return humanizeWords(normalizedType).join(' ');
};

/**
 * Human-readable PLURAL name for a fixture category — the contract the
 * "Apply to all <name>" control depends on.
 */
const fixtureTypeDisplayName = (normalizedType) => {
  if (DISPLAY_NAMES[normalizedType]) return DISPLAY_NAMES[normalizedType];
  if (!normalizedType) return '';
  const words = humanizeWords(normalizedType);
  if (!words.length) return '';
  words[words.length - 1] = pluralizeWord(words[words.length - 1]);
  return words.join(' ');
};

/**
 * Whether `candidate` belongs to the same "apply to all" group as
 * `selectedObject`. Same normalized type — AND, for lights (pendants and
 * recessed cans share the `ceiling-light` type), the same `lightingSubtype`.
 * So selecting a kitchen pendant groups only with other pendants, not with the
 * recessed cans on the same ceiling.
 */
const isSameFixtureGroup = (selectedObject, candidate) => {
  if (!selectedObject || !candidate) return false;
  if (getObjectType(candidate) !== getObjectType(selectedObject)) return false;
  const a = selectedObject.lightingSubtype;
  const b = candidate.lightingSubtype;
  if (a || b) return a === b;
  return true;
};

/**
 * Display name for the "apply to all" group of `selectedObject`. Pendants read
 * as "Pendant Lights" rather than the generic "Ceiling Lights".
 */
const fixtureGroupDisplayName = (selectedObject) => {
  if (selectedObject?.lightingSubtype === 'pendant') return 'Pendant Lights';
  return fixtureTypeDisplayName(getObjectType(selectedObject));
};

// -----------------------------------------------------------------------------
// Cabinet grouping helpers — used by the panel's "Select all cabinets" /
// "Select all lowers" / "Select all uppers" affordance. Cabinet-like types are
// everything the user logically thinks of as "cabinetry" for the purpose of
// applying a unified finish / door style / paint at once.
// -----------------------------------------------------------------------------

/** All exact object types that count as "cabinetry" for bulk-apply. */
const CABINET_LIKE_TYPES = new Set([
  'lower-cabinet',
  // The catalog vanity body (#1535/L9) — its assembled twin is
  // 'lower-cabinet' and was always included.
  'vanity-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'blank-panel',
  'fridge-surround',
  'laundry-surround',
  'oven-surround',
]);

/** True if the given raw object type string is one of the cabinet-like types. */
const isCabinetLikeType = (type) => {
  if (!type) return false;
  return CABINET_LIKE_TYPES.has(String(type).toLowerCase());
};

/** True if `obj`'s raw type is cabinet-like. */
const isCabinetLikeObject = (obj) => isCabinetLikeType(getObjectType(obj));

/**
 * Box types that expose the full cabinet cosmetic editing in the properties
 * panel (Finish / Door Style / Hardware / Texture + the room/run/unit appearance
 * scope). Narrower than CABINET_LIKE_TYPES — blank panels and fridge surrounds
 * count as "cabinetry" for bulk-apply but don't carry door styles. Corner
 * cabinets resolve to 'corner-cabinet'/'corner-fill-cabinet' (they also carry
 * 'lower-cabinet'), so they're enumerated here to get the same controls as a
 * straight-run bay.
 */
const CABINET_COSMETIC_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
]);

/** True if the raw object type exposes the full cabinet cosmetic editing. */
const isCabinetCosmeticType = (type) => {
  if (!type) return false;
  return CABINET_COSMETIC_TYPES.has(String(type).toLowerCase());
};

/**
 * Heuristic: above this Y-center (meters) a corner cabinet without an
 * explicit `dimensions.isUpper` flag is treated as an upper. Upper cabinets
 * typically center at ~1.27–1.52 m above the floor; lowers center ~0.46 m.
 * 1.0 m is a safe midpoint that catches uppers regardless of ceiling height.
 */
const CORNER_UPPER_Y_THRESHOLD = 1.0;

/** True if the corner is positioned/flagged as an upper. */
const isCornerUpper = (obj) => {
  if (obj?.dimensions?.isUpper === true) return true;
  // Fall back to position: some materialized corner cabinets don't carry the
  // dimensions.isUpper flag explicitly. Use Y-center as a tiebreaker so
  // "select all uppers" reliably includes them.
  const y = obj?.center?.y;
  return typeof y === 'number' && y > CORNER_UPPER_Y_THRESHOLD;
};

/**
 * True if `obj` is in the "lowers" group: lower-cabinet, OR
 * corner-cabinet/corner-fill-cabinet that aren't uppers (see `isCornerUpper`).
 * blank-panel and fridge-surround are intentionally excluded here — they live
 * in the "all" bucket only.
 */
const isLowerCabinetLikeObject = (obj) => {
  if (!obj) return false;
  const t = getObjectType(obj);
  if (t === 'lower-cabinet') return true;
  if (t === 'corner-cabinet' || t === 'corner-fill-cabinet') {
    return !isCornerUpper(obj);
  }
  return false;
};

/**
 * True if `obj` is in the "uppers" group: upper-cabinet, OR
 * corner-cabinet/corner-fill-cabinet flagged or positioned as upper.
 */
const isUpperCabinetLikeObject = (obj) => {
  if (!obj) return false;
  const t = getObjectType(obj);
  if (t === 'upper-cabinet') return true;
  if (t === 'corner-cabinet' || t === 'corner-fill-cabinet') {
    return isCornerUpper(obj);
  }
  return false;
};

/**
 * Return the IDs of all cabinet-like objects in `room` matching the scope.
 *   scope === 'all'    → every cabinet-like type
 *   scope === 'lowers' → only lower-side cabinets (see isLowerCabinetLikeObject)
 *   scope === 'uppers' → only upper-side cabinets (see isUpperCabinetLikeObject)
 *
 * Excludes nothing by default — callers that want to exclude the focused
 * cabinet should filter the result themselves.
 */
const findCabinetGroupIds = (scope, room) => {
  if (!room?.objects) return [];
  const predicate =
    scope === 'lowers'
      ? isLowerCabinetLikeObject
      : scope === 'uppers'
        ? isUpperCabinetLikeObject
        : isCabinetLikeObject;
  return room.objects.filter(predicate).map((o) => o.id);
};

// getObjectType and GENERIC_TYPES are re-exported as `export const` at the
// top of this file (necessary because @shared-lib/fixture-types is CJS).

module.exports = {
  getObjectType,
  GENERIC_TYPES,
  normalizeFixtureType,
  fixtureTypeDisplayNameSingular,
  fixtureTypeDisplayName,
  isSameFixtureGroup,
  fixtureGroupDisplayName,
  CABINET_LIKE_TYPES,
  isCabinetLikeType,
  isCabinetLikeObject,
  CABINET_COSMETIC_TYPES,
  isCabinetCosmeticType,
  isLowerCabinetLikeObject,
  isUpperCabinetLikeObject,
  findCabinetGroupIds,
};
